"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import { useCursorContext } from "../components/CursorProvider"

interface CursorTrailProps {
  /** Number of trail dots to render */
  count?: number
  /** CSS color string for the dots */
  color?: string
  /** Diameter of the leading dot in px */
  size?: number
  /** z-index; should sit just below the main cursor */
  zIndex?: number
}

export const CursorTrail: React.FC<CursorTrailProps> = ({
  count = 12,
  color = "rgba(255, 255, 255, 0.6)",
  size = 8,
  zIndex = 9998,
}) => {
  const { cursorState } = useCursorContext()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Use direct mousemove listener for full-fidelity position tracking
  const mouseRef = useRef({ x: 0, y: 0 })
  // Ring buffer of positions
  const historyRef = useRef<Array<{ x: number; y: number }>>([])
  const rafRef = useRef<number | null>(null)
  const variantRef = useRef(cursorState.variant)

  useEffect(() => {
    variantRef.current = cursorState.variant
  }, [cursorState.variant])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    const onMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener("mousemove", onMove, { passive: true })

    const animate = () => {
      // Prepend latest position to history and cap at `count`
      historyRef.current = [{ ...mouseRef.current }, ...historyRef.current].slice(0, count)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      historyRef.current.forEach((dot, i) => {
        const progress = i / count          // 0 = newest, approaches 1 = oldest
        const alpha = (1 - progress) * 0.7
        const radius = (size / 2) * (1 - progress * 0.5)

        ctx.beginPath()
        ctx.arc(dot.x, dot.y, Math.max(radius, 0.5), 0, Math.PI * 2)
        ctx.globalAlpha = alpha
        ctx.fillStyle = color
        ctx.fill()
      })

      ctx.globalAlpha = 1
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener("resize", resize)
      window.removeEventListener("mousemove", onMove)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [count, color, size])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex,
      }}
    />
  )
}
